/**
 * لاگِ فعالیتِ توسعه — «این نشست دقیقاً چه کرد، و هر کارش چقدر طول کشید».
 *
 * جدولش `dev_log` است (مایگریشن 0071)، خواندنش `npm run log:pull`، و پاک
 * کردنش `npm run log:clear` که با هر مرج اجرا می‌شود. بخشِ خالص و آزموده‌ی
 * منطق در `devlogRow.ts` است؛ این فایل فقط لایه‌ی عارضه‌دار است: حالت، تایمر،
 * شبکه.
 *
 * ═══ چه چیزهایی خودشان می‌نشینند ═══
 *
 * هیچ صفحه‌ای لازم نیست چیزی صدا بزند. چهار مسیر از قبل وصل‌اند:
 *   • **کوئری** — `lib/supabase` هر `from(...).select/insert/...` و هر `rpc`
 *     را زمان می‌گیرد و به این‌جا می‌دهد (نگاه کن به `observeQueries`).
 *   • **کارِ کاربر** — کیتِ هر دو اپ هندلرهایش را با `tapped()` می‌پیچد، پس
 *     هر دکمه و چیپ و سوییچی که از کیت آمده خودش ثبت می‌شود.
 *   • **صفحه** — ردیابِ مسیرِ هر اپ `markNav()` را صدا می‌زند.
 *   • **خطا** — `reportError` و پوششِ `console.warn/error`.
 *
 * ═══ فقط یک حساب — برای فرستادن ═══
 *
 * تا وقتی نامِ کاربریِ واردشده `TRACE_OWNER` نباشد، هیچ سطری **به این جدول**
 * فرستاده نمی‌شود. دلیلش حجم است نه راز: لاگی که «همه‌چیز» را می‌نویسد،
 * ضربدرِ تعدادِ آدم‌ها، نه خواندنی است نه نگه‌داشتنی. سیاستِ خودِ جدول هم
 * همین را جدا تضمین می‌کند.
 *
 * ولی خودِ **ساختنِ** ردیف برای همه اتفاق می‌افتد، چون خریدارِ دومی هم دارد:
 * `lib/diagLog` همین ردیف‌ها را روی دستگاهِ هر کاربر نگه می‌دارد تا اگر روزی
 * مشکلی گزارش کرد، بشود دید چه شده. آن یکی هیچ‌وقت خودبه‌خود به شبکه نمی‌زند.
 * نگاه کن به `wanted()` پایین.
 *
 * ═══ سطرهای قبل از اینکه بدانیم کی هستی ═══
 *
 * بالا آمدنِ اپ، صفحه‌ی ورود، و اولین کوئری‌ها همه قبل از رسیدنِ پروفایل
 * اتفاق می‌افتند. اگر همان لحظه تصمیم می‌گرفتیم، دقیقاً کندترین بخشِ هر
 * نشست — خودِ بالا آمدن — هیچ‌وقت لاگ نمی‌شد. پس تا مشخص شدنِ هویت در یک
 * بافرِ کوچک می‌نشینند: صاحبش که بود می‌روند توی صف، نبود دور ریخته می‌شوند.
 */
import { supabase, observeQueries } from './supabase.ts';
import { isDemoMode } from './demo.ts';
import { platformInfo } from '../platform.ts';
import { TRACE_OWNER } from './trace.ts';
import { makeQueue } from './analyticsQueue.ts';
import { captureDiag, diagOn } from './diagLog.ts';
import {
  isSilentTable, newDevSession, queryName, trimDetail, type DevLogKind, type DevLogRow,
} from './devlogRow.ts';

/** بعد از این تعداد سطر بدونِ انتظار می‌رود. */
const BATCH = 20;
/** و اگر به آن نرسید، حداکثر این‌قدر صبر می‌کند. */
const FLUSH_MS = 5_000;
/** سقفِ صف. شبکه که قطع باشد، تازه‌ها می‌مانند و قدیمی‌ها می‌افتند. */
const MAX_QUEUE = 400;
/**
 * سقفِ کلِ یک نشست. یک حلقه‌ی رندرِ خراب می‌تواند در چند ثانیه هزاران سطر
 * بسازد؛ آن‌وقت هم جدول پر می‌شود هم خودِ لاگ می‌شود مسئله. سطرِ آخر خودش
 * می‌گوید که سقف خورده — یک لاگِ ساکت‌شده که نگوید ساکت شده بدترین حالت است.
 */
const MAX_ROWS = 5_000;
/**
 * فاصله‌ی بینِ زدنِ یک دکمه و باز شدنِ صفحه‌ی بعد. بیشتر از این، دیگر آن تپ
 * دلیلِ این ناوبری نبوده — نگاه کن به `markNav`.
 */
const TAP_WINDOW = 3_000;

/** ساعتِ یکنواخت، اگر باشد. برای اندازه‌گیریِ مدت، `Date.now` قابل اتکا نیست. */
const now = (): number => {
  const p = (globalThis as { performance?: { now?: () => number } }).performance;
  return p?.now ? p.now() : Date.now();
};

const startedAt = now();

/**
 * `unknown` یعنی هنوز نمی‌دانیم کی وارد شده — سطرها در بافر می‌مانند.
 * `on`/`off` یعنی می‌دانیم.
 */
let state: 'unknown' | 'on' | 'off' = 'unknown';
let userId: string | null = null;

const session = newDevSession();
let seq = 0;
let rowCount = 0;

const queue = makeQueue<DevLogRow>(BATCH, MAX_QUEUE);
/** سطرهای قبل از شناسایی. همان سقفِ صف را دارد و همان‌جور قدیمی‌ها را می‌اندازد. */
let buffer: DevLogRow[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

/** آخرین کاری که کاربر کرد، برای وصل کردنِ «زدم» به «باز شد». */
let lastTap: { name: string; at: number } | null = null;
let lastScreen: string | null = null;

/**
 * آیا این سطر اصلاً خریداری دارد.
 *
 * دو خریدار هست و هرکدام سهمِ خودش را می‌برد: همین فایل، که برای صاحبِ لاگ
 * روی شبکه می‌فرستد (`state !== 'off'` — یا وارد است یا هنوز معلوم نیست)، و
 * `lib/diagLog` که برای **همه** روی خودِ دستگاه نگه می‌دارد و فقط با اجازه‌ی
 * صریحِ کاربر از صفحه‌ی بازخورد می‌فرستد. ساختنِ ردیف یکی است، مقصدها دوتا —
 * و همین است که یعنی یک دکمه‌ی تازه یا یک صفحه‌ی تازه، برای هر دو با هم لاگ
 * می‌شود.
 *
 * سقفِ `MAX_ROWS` مشترک است و عمداً: یک حلقه‌ی رندرِ خراب که ثانیه‌ای هزار
 * سطر می‌سازد، باید هر دو را ساکت کند نه یکی را.
 */
const wanted = (): boolean => !isDemoMode && rowCount < MAX_ROWS && (state !== 'off' || diagOn());

/** آیا لاگ روشن و شناخته‌شده است — برای جاهایی که فقط «الان بفرست» معنی دارد. */
export const devLogOn = (): boolean => state === 'on';

/* ─────────────────────────── نوشتن ─────────────────────────── */

export interface DevLogOptions {
  /** مدتِ زمان به میلی‌ثانیه. گرد می‌شود؛ کسرِ میلی‌ثانیه چیزی نمی‌گوید. */
  ms?: number;
  ok?: boolean;
  detail?: Record<string, unknown>;
}

/** یک سطرِ تازه. هیچ‌وقت throw نمی‌کند و هیچ‌وقت منتظر نمی‌ماند. */
export function dlog(kind: DevLogKind, name: string, opts: DevLogOptions = {}): void {
  if (!wanted()) return;
  try {
    rowCount += 1;
    seq += 1;
    const p = platformInfo();
    const row: DevLogRow = {
      at: new Date().toISOString(),
      session_id: session,
      seq,
      surface: p.isWeb ? 'web' : 'mobile',
      kind,
      name: name.slice(0, 160),
      ms: opts.ms === undefined ? null : Math.round(opts.ms),
      ok: opts.ok === undefined ? null : opts.ok,
      detail: trimDetail(opts.detail),
      app_version: p.appVersion,
      platform: `${p.os} ${p.osVersion}`,
      user_id: userId,
    };

    // سقف که خورد، یک سطرِ آخر می‌گوید چرا بقیه نیستند.
    if (rowCount === MAX_ROWS) {
      row.detail = { ...row.detail, truncated: `سقفِ ${MAX_ROWS} سطر در این نشست` };
    }

    // اول لاگِ روی دستگاه، چون برای همه است و هیچ شرطی جز روشن بودنش ندارد.
    captureDiag(row);

    if (state === 'on') {
      const full = queue.push(row);
      if (full) void send(full);
      else schedule();
      return;
    }
    // `off` یعنی این سطر فقط مالِ لاگِ روی دستگاه بود؛ نگه داشتنش این‌جا
    // فقط حافظه می‌گیرد برای صفی که هیچ‌وقت فرستاده نمی‌شود.
    if (state !== 'unknown') return;
    buffer.push(row);
    if (buffer.length > MAX_QUEUE) buffer = buffer.slice(-MAX_QUEUE);
  } catch {
    // خودِ لاگ کردن هیچ‌وقت نباید مسیرِ کاربر را بشکند.
  }
}

function schedule(): void {
  if (timer) return;
  timer = setTimeout(() => { timer = null; void send(queue.take()); }, FLUSH_MS);
}

/**
 * هرچه در صف مانده همین حالا برود — برای رفتنِ اپ به پس‌زمینه یا پنهان شدنِ تب.
 * بدونِ این، سطرهای آخرِ هر نشست گم می‌شوند و آن‌ها معمولاً همان‌هایی‌اند که
 * دنبالشان می‌گردی.
 */
export function flushDevLog(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  void send(queue.take());
}

let warned = false;

async function send(rows: DevLogRow[]): Promise<void> {
  if (!rows.length || state !== 'on') return;
  try {
    const { error } = await supabase.from('dev_log').insert(rows);
    /**
     * خطا فقط **یک بار** به کنسول می‌رود و از `reportError` رد نمی‌شود:
     * `reportError` خودش یک سطرِ لاگ می‌سازد و آن سطر باز از همین‌جا رد
     * می‌شود. یک لاگِ خرابی که خودش را تکثیر کند، از نبودنش بدتر است.
     *
     * ولی کاملاً ساکت هم نه — لاگی که بی‌صدا نمی‌رسد یعنی یک عصر گشتن دنبالِ
     * چیزی که اصلاً ثبت نشده. شایع‌ترین دلیلش هم مشخص است: مایگریشن 0071
     * روی این دیتابیس اجرا نشده.
     */
    if (error && !warned) {
      warned = true;
      console.log(`[devlog] سطرها ثبت نشدند: ${error.message} — مایگریشن 0071 اجرا شده؟`);
    }
  } catch {
    /* شبکه نبود؛ همین سطرها می‌افتند و نشستِ بعد ادامه می‌دهد. */
  }
}

/* ─────────────────────────── کی وارد است ─────────────────────────── */

/**
 * `SessionProvider` بعد از رسیدنِ پروفایل صدایش می‌زند (و با `null` وقتی
 * کسی وارد نیست).
 *
 * تنها جایی که لاگ روشن می‌شود همین است. `username` و نه `is_admin`: به همان
 * دلیلی که در `trace.ts` نوشته شده — ادمینِ دومی که فردا اضافه شود نباید
 * ناگهان شروع کند به نوشتنِ هر تپش.
 */
export function setDevLogUser(id: string | null, username?: string | null): void {
  const on = !isDemoMode && !!id && username === TRACE_OWNER;
  if (on && state !== 'on') {
    state = 'on';
    userId = id;
    // سطرهای قبل از شناسایی مالِ همین نشست و همین آدم‌اند؛ فقط شناسه‌شان را
    // نداشتند. سیاستِ جدول `user_id = auth.uid()` می‌خواهد، پس بدونِ این
    // مهرزدن، همه‌شان بی‌صدا رد می‌شدند.
    const carried = buffer.map((r) => ({ ...r, user_id: id }));
    buffer = [];
    for (const r of carried) {
      // خروجیِ `push` دورانداختنی نیست: دسته که پر شود خودش را می‌دهد و صف
      // را خالی می‌کند، پس نادیده گرفتنش یعنی گم شدنِ همان بیست سطر.
      const full = queue.push(r);
      if (full) void send(full);
    }
    dlog('app', 'start', {
      ms: now() - startedAt,
      // `build` این‌جاست تا هیچ‌وقت لازم نشود حدس بزنیم گوشی کدام بسته را
      // اجرا می‌کند — نگاه کن به `PlatformInfo.buildId`.
      detail: { session, note: 'تا شناختنِ کاربر', build: platformInfo().buildId ?? null },
    });
    flushDevLog();
    return;
  }
  if (!on && state !== 'off') {
    // یا کسِ دیگری وارد شده یا هیچ‌کس؛ در هر دو حالت چیزی جمع نمی‌کنیم.
    // `unknown` هم به `off` می‌رود: تا وقتی نشستی نیست، هویتی هم نمی‌آید.
    state = 'off';
    buffer = [];
    userId = null;
  }
}

/* ─────────────────────────── کارِ کاربر ─────────────────────────── */

const isPromise = (v: unknown): v is Promise<unknown> =>
  typeof (v as { then?: unknown } | null)?.then === 'function';

const errText = (e: unknown): string =>
  (e instanceof Error ? e.message : String(e)).slice(0, 200);

/**
 * ناظرِ لمس — `lib/analytics` خودش را این‌جا ثبت می‌کند.
 *
 * ═══ چرا ثبت‌کننده، و نه یک import ═══
 *
 * جهتِ وابستگی باید یک‌طرفه بماند. `report.ts` همین فایل را import می‌کند و
 * `analytics.ts` هم `report.ts` را؛ اگر این فایل مستقیم `analytics` را صدا
 * می‌زد، حلقه‌ی `analytics → report → devlog → analytics` بسته می‌شد. همان
 * الگویی که چند خط پایین‌تر برای `observeQueries` هم به‌کار رفته و دلیلش
 * همان‌جا نوشته شده.
 */
let tapSink: ((label: string) => void) | null = null;
export const observeTaps = (fn: (label: string) => void): void => { tapSink = fn; };

/**
 * هندلرِ یک دکمه را می‌پیچد تا خودش ثبت شود.
 *
 * کیتِ هر دو اپ از همین استفاده می‌کند (`ui/kit`), پس «هر کاری که کاربر
 * می‌کند» با چند خط پوشش داده می‌شود و نه با دست‌کاریِ هر صفحه.
 *
 * `ms` یعنی خودِ هندلر چقدر طول کشید — و اگر Promise برگرداند، تا ته کارش.
 * دکمه‌ای که نیم ثانیه کار می‌کند، نیم ثانیه رابط را قفل کرده.
 *
 * ⚠ **دو مصرف‌کننده دارد و شرطشان یکی نیست.** لاگِ توسعه فقط برای صاحبِ
 * ابزار نوشته می‌شود (`wanted()`)، ولی آمارِ رفتاری برای همه — «کاربر روی
 * این صفحه چه دکمه‌ای زد» سوالی است که جوابش از دستگاهِ خودمان درنمی‌آید.
 * پس پوشش وقتی برداشته می‌شود که **هیچ‌کدام** نخواهند؛ وگرنه ثبتِ آماری
 * برای همه‌ی کاربران بی‌صدا از کار می‌افتاد — و این باگی است که هیچ تستی
 * نمی‌گیردش، چون در نبودِ صاحبِ ابزار همه‌چیز «کار می‌کند».
 */
export function tapped<F extends ((...args: never[]) => unknown) | undefined>(
  label: string, fn: F,
): F {
  if (!fn || (!wanted() && !tapSink)) return fn;
  const inner = fn as (...args: unknown[]) => unknown;
  const wrapped = (...args: unknown[]): unknown => {
    const t0 = now();
    lastTap = { name: label, at: t0 };
    // ⚠ پیش از خودِ هندلر: هندلری که صفحه را عوض می‌کند باید لمسش **قبل**
    // از `screen_view`ِ بعدی ثبت شده باشد، وگرنه ترتیبِ سفر جابه‌جا می‌شود.
    try { tapSink?.(label); } catch { /* آمار هیچ‌وقت مزاحمِ دکمه نمی‌شود */ }
    try {
      const out = inner(...args);
      if (isPromise(out)) {
        void out.then(
          () => dlog('tap', label, { ms: now() - t0, ok: true }),
          (e: unknown) => dlog('tap', label, { ms: now() - t0, ok: false, detail: { error: errText(e) } }),
        );
      } else {
        dlog('tap', label, { ms: now() - t0 });
      }
      return out;
    } catch (e) {
      dlog('tap', label, { ms: now() - t0, ok: false, detail: { error: errText(e) } });
      throw e;
    }
  };
  // دو مرحله‌ای، چون امضای پوشش عمداً از خودِ `F` عام‌تر است: هر آرگومانی را
  // می‌گیرد و دست‌نخورده رد می‌کند. تایپ‌اسکریپت این را «شاید اشتباه» می‌بیند
  // و همین‌جا صریح می‌گوییم که نیست.
  return wrapped as unknown as F;
}

/* ─────────────────────────── صفحه‌ها ─────────────────────────── */

/** یک فریم بعد — یعنی وقتی صفحه واقعاً رسم شده، نه وقتی React تمام کرده. */
function afterFrame(fn: () => void): void {
  const raf = (globalThis as { requestAnimationFrame?: (cb: () => void) => unknown })
    .requestAnimationFrame;
  if (raf) raf(fn); else setTimeout(fn, 0);
}

/**
 * «رفتم به این صفحه، و این‌قدر طول کشید تا بیاید.»
 *
 * ═══ از کِی تا کِی ═══
 *
 * از لحظه‌ای که کاربر **خواستش** — یعنی همان تپی که مسیر را عوض کرد، اگر
 * همین حالا بوده — تا اولین فریمی که صفحه رویش رسم شده. اگر ناوبری از تپ
 * نیامده (لینکِ مستقیم، بازگشتِ مرورگر، ریدایرکت) از همین لحظه شمرده می‌شود
 * و عدد کوچک‌تر و بی‌معنی‌تر است؛ `via` توی `detail` می‌گوید کدام حالت بوده.
 *
 * این عدد **رسیدنِ داده** نیست، رسیدنِ خودِ صفحه است. اینکه فهرست چقدر بعد
 * پر شد را سطرهای `query`ِ دقیقاً بعدِ همین سطر می‌گویند.
 */
export function markNav(screen: string): void {
  if (!wanted()) return;
  const tap = lastTap && now() - lastTap.at < TAP_WINDOW ? lastTap : null;
  const from = lastScreen;
  const started = tap ? tap.at : now();
  lastScreen = screen;
  afterFrame(() => {
    dlog('nav', screen, {
      ms: now() - started,
      detail: { via: tap ? tap.name : 'direct', ...(from ? { from } : {}) },
    });
  });
}

/* ─────────────────────────── کوئری‌ها ─────────────────────────── */

/**
 * وصل کردنِ زمان‌گیرِ `lib/supabase` به این لاگ.
 *
 * در همین سطحِ ماژول صدا زده می‌شود و نه از یک `init...()`: هر اپی که یک
 * سطرِ لاگ می‌نویسد این فایل را import کرده، پس ثبتِ ناظر هم همان‌جا انجام
 * شده. یک قدمِ راه‌اندازیِ فراموش‌شدنیِ کمتر در هر اپ.
 *
 * جهتِ وابستگی هم عمدی است: `devlog → supabase` و نه برعکس. اگر
 * `lib/supabase` این‌جا را import می‌کرد، دو ماژول به هم گره می‌خوردند —
 * برای چیزی که یک تابعِ ثبت‌شده حلش می‌کند.
 */
observeQueries((table, op, ms, ok, detail, label) => {
  if (isSilentTable(table)) return;
  // صفحه‌ای که درخواست از آن رفته (WHOSUP-325) — همان نامی که `markNav` گرفته.
  const where = lastScreen ? { screen: lastScreen } : {};
  dlog('query', queryName(table, op, label), { ms, ok, detail: { ...where, ...detail } });
});

/* ─────────────────────────── کنسول ─────────────────────────── */

/**
 * `console.warn`/`console.error` هم توی لاگ بنشینند.
 *
 * قرینه‌ی `installTraceConsole` است و کنارِ هم زندگی می‌کنند: آن یکی چهارده
 * سطرِ آخر را روی صفحه‌ی گوشی نشان می‌دهد، این یکی همه‌شان را برای بعد نگه
 * می‌دارد. هر کدام پوششِ خودش را می‌گذارد و اصلی را صدا می‌زند، پس ترتیبشان
 * مهم نیست.
 *
 * یک بار موقعِ بالا آمدنِ اپ صدا زده می‌شود.
 */
export function installDevLogConsole(): void {
  const c = console as unknown as Record<string, (...args: unknown[]) => void>;
  for (const level of ['warn', 'error'] as const) {
    const original = c[level];
    c[level] = (...args: unknown[]) => {
      original(...args);
      const text = args.map((a) => (typeof a === 'string' ? a : String(a))).join(' ');
      // خطاهای خودِ اپ از `reportError` رد می‌شوند و آن‌جا سطرِ `error`ِ
      // خواناترِ خودشان را می‌نویسند؛ بدونِ این، هر خطا دو سطر می‌شد.
      if (text.startsWith('[goout:') || text.startsWith('[devlog]')) return;
      dlog('console', level, { detail: { text } });
    };
  }
}
